import arriereBackoffice, {
  CaseCreationPolicy,
  CaseForm,
  CasePriority,
  CreateTaxonomyClassificationInput,
  PaginationInput,
  TaxonomyLegacyPathInput,
  TaxonomyNature,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'

export interface SearchTaxonomyClassificationsParams {
  /** Taxonomy nodes of any level: OR within a level, AND across levels. */
  nodeIds?: string[]
  natures?: TaxonomyNature[]
  caseForms?: CaseForm[]
  creationPolicies?: CaseCreationPolicy[]
  priorities?: CasePriority[]
  searchTerm?: string
  pagination: PaginationInput
  /** The home whose contract answers each classification's Bill To. */
  homeId?: string
}

const CLASSIFICATION_FIELDS = {
  taxonomyId: true,
  task: true,
  serviceType: true,
  category: true,
  subcategory: true,
  nature: true,
  caseForm: true,
  executionMode: true,
  creationPolicy: true,
  defaultPriority: true,
  description: true,
  keyIdentificationMarkers: true,
  scope: true,
  exclusions: true,
  createdAt: true,
  updatedAt: true,
  billTo: {
    contractExpenseCategory: true,
    includedPrograms: true,
  },
  legacyPath: {
    recordType: true,
    caseType: true,
    category: true,
    subcategory: true,
  },
} as const

const LEGACY_PATH_FIELDS = {
  recordType: true,
  caseType: true,
  category: true,
  subcategory: true,
} as const

export const searchClassifications = async ({
  homeId,
  ...searchArgs
}: SearchTaxonomyClassificationsParams) => {
  const response = await arriereBackoffice.queries.taxonomy({
    __alias: 'searchTaxonomyClassifications',
    searchTaxonomyClassifications: {
      __args: searchArgs,
      total: true,
      pages: true,
      results: {
        ...CLASSIFICATION_FIELDS,
        homeBillTo: { __args: { homeId } },
      },
    },
  })

  return response.searchTaxonomyClassifications
}

/** Every active classification's Bill To, keyed by classification id (the add form's prefill). */
export const getClassificationBillTos = async () => {
  const response = await arriereBackoffice.queries.taxonomy({
    __alias: 'taxonomyClassificationBillTos',
    taxonomyClassifications: {
      taxonomyId: true,
      billTo: {
        contractExpenseCategory: true,
        includedPrograms: true,
      },
    },
  })

  return response.taxonomyClassifications
}

export const getLegacyInputs = async () => {
  const response = await arriereBackoffice.queries.taxonomy({
    __alias: 'taxonomyLegacyInputs',
    taxonomyLegacyInputs: {
      recordTypes: {
        recordType: true,
        caseTypes: {
          label: true,
          categories: {
            code: true,
            label: true,
            subcategories: {
              code: true,
              label: true,
            },
          },
        },
      },
    },
  })

  return response.taxonomyLegacyInputs.recordTypes
}

export const previewLegacyPath = async (parentNodeId: string) => {
  const response = await arriereBackoffice.queries.taxonomy({
    __alias: 'taxonomyLegacyPathPreview',
    taxonomyLegacyPathPreview: {
      __args: { parentNodeId },
      ...LEGACY_PATH_FIELDS,
    },
  })

  return response.taxonomyLegacyPathPreview
}

export const getClassificationsOnLegacyPath = async (path: TaxonomyLegacyPathInput) => {
  const response = await arriereBackoffice.queries.taxonomy({
    __alias: 'taxonomyClassificationsOnLegacyPath',
    taxonomyClassificationsOnLegacyPath: {
      __args: { path },
      ...CLASSIFICATION_FIELDS,
    },
  })

  return response.taxonomyClassificationsOnLegacyPath
}

export const findSimilarClassifications = async (label: string, limit: number) => {
  const response = await arriereBackoffice.queries.taxonomy({
    __alias: 'similarTaxonomyClassifications',
    similarTaxonomyClassifications: {
      __args: { label, limit },
      ...CLASSIFICATION_FIELDS,
    },
  })

  return response.similarTaxonomyClassifications
}

export const classifyDescription = async (description: string) => {
  const response = await arriereBackoffice.queries.taxonomy({
    __alias: 'classifyTaxonomyDescription',
    classifyCasesFromDescription: {
      __retry: false,
      __args: { description },
      confidence: true,
      classification: CLASSIFICATION_FIELDS,
    },
  })

  return response.classifyCasesFromDescription
}

export const createClassification = async (input: CreateTaxonomyClassificationInput) => {
  const response = await arriereBackoffice.mutations.taxonomy({
    __alias: 'createTaxonomyClassification',
    createTaxonomyClassification: {
      __retry: false,
      __args: { input },
      ...CLASSIFICATION_FIELDS,
    },
  })

  return response.createTaxonomyClassification
}
